import { ChevronDown, ChevronUp } from "lucide-react";
import { forwardRef, useCallback, useEffect, useState, useRef } from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { Button } from "./button";
import { Input } from "./input";

export interface NumberInputProps
    extends Omit<NumericFormatProps, "value" | "onValueChange"> {
    stepper?: number;
    thousandSeparator?: string;
    placeholder?: string;
    defaultValue?: number;
    min?: number;
    max?: number;
    value?: number; // Controlled value
    suffix?: string;
    prefix?: string;
    suffixLabel?: string; // Label displayed on the right side outside the input
    onValueChange?: (value: number | undefined) => void;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
    (
        {
            stepper = 1,
            thousandSeparator,
            placeholder,
            defaultValue,
            min = -Infinity,
            max = Infinity,
            onValueChange,
            fixedDecimalScale = false,
            decimalScale = 0,
            suffix,
            prefix,
            suffixLabel,
            value: controlledValue,
            ...props
        },
        ref
    ) => {
        const [value, setValue] = useState<number | undefined>(
            controlledValue ?? defaultValue
        );

        const inputRef = useRef<HTMLInputElement | null>(null);
        const isUserInteraction = useRef(false);

        useEffect(() => {
            if (ref) {
                if (typeof ref === "function") {
                    ref(inputRef.current!);
                } else {
                    (
                        ref as React.MutableRefObject<HTMLInputElement | null>
                    ).current = inputRef.current;
                }
            }
        }, [ref]);

        const handleIncrement = useCallback(() => {
            const next =
                value === undefined
                    ? stepper
                    : Math.min(value + stepper, max);
            setValue(next);
            onValueChange?.(next);
        }, [value, stepper, max, onValueChange]);

        const handleDecrement = useCallback(() => {
            const next =
                value === undefined
                    ? -stepper
                    : Math.max(value - stepper, min);
            setValue(next);
            onValueChange?.(next);
        }, [value, stepper, min, onValueChange]);

        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                const el = inputRef.current;
                if (!el) return;
                if (document.activeElement === el) {
                    if (e.key === "ArrowUp") {
                        e.preventDefault();
                        handleIncrement();
                    } else if (e.key === "ArrowDown") {
                        e.preventDefault();
                        handleDecrement();
                    }
                }
            };

            window.addEventListener("keydown", handleKeyDown);
            return () => {
                window.removeEventListener("keydown", handleKeyDown);
            };
        }, [handleIncrement, handleDecrement]);

        // Sync with controlled value (when parent changes value prop)
        // Only depend on controlledValue — not `value`, which would cause infinite loops
        useEffect(() => {
            if (controlledValue !== undefined) {
                setValue(controlledValue);
            }
        }, [controlledValue]);

        const handleChange = useCallback((values: {
            value: string;
            floatValue: number | undefined;
        }) => {
            const newValue =
                values.floatValue === undefined ? undefined : values.floatValue;
            setValue(newValue);

            // Only call onValueChange if this is a user interaction
            // NumericFormat calls onValueChange even when value prop changes
            // We use isUserInteraction flag to distinguish actual user input
            if (isUserInteraction.current && onValueChange) {
                onValueChange(newValue);
            }
        }, [onValueChange]);

        // Mark as user interaction when input is focused and typed
        const handleFocus = () => {
            isUserInteraction.current = true;
        };

        const handleBlur = useCallback(() => {
            // Reset flag on blur
            isUserInteraction.current = false;

            if (value !== undefined) {
                let clampedValue = value;
                if (value < min) {
                    clampedValue = min;
                    setValue(min);
                } else if (value > max) {
                    clampedValue = max;
                    setValue(max);
                }

                // Call onValueChange if value was clamped
                if (clampedValue !== value && onValueChange) {
                    onValueChange(clampedValue);
                }
            }
        }, [value, min, max, onValueChange]);

        return (
            <div className="flex items-center relative">
                <NumericFormat
                    value={value}
                    onValueChange={handleChange}
                    thousandSeparator={thousandSeparator}
                    decimalScale={decimalScale}
                    fixedDecimalScale={fixedDecimalScale}
                    allowNegative={min < 0}
                    valueIsNumericString
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    suffix={suffix}
                    prefix={prefix}
                    customInput={Input}
                    placeholder={""}
                    className="bg-background border border-border text-foreground text-[11px] h-8 px-2 py-1 pr-10 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none rounded-r-none relative focus-visible:[--tw-ring-offset-width:none]"
                    getInputRef={(inst: any) => {
                        inputRef.current = inst;
                    }}
                    {...props}
                />

                {/* Custom placeholder positioned on the right when empty */}
                {(value === undefined || value === null) && placeholder && (
                    <div className="absolute right-[1.65rem] top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                        <p className="text-xs font-medium">{placeholder}</p>
                    </div>
                )}

                {/* Suffix label positioned like ClearableInput icon */}
                {suffixLabel && (
                    <div className="absolute right-[1.65rem] top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                        <p className="text-xs font-medium">{suffixLabel}</p>
                    </div>
                )}

                <div className="flex flex-col">
                    <Button
                        aria-label="Increase value"
                        className="bg-muted text-foreground text-[11px] px-1 h-4 min-h-0 rounded-l-none rounded-br-none border-input border-l-0 border-b-[0.5px] focus-visible:relative border-none cursor-pointer hover:!bg-primary hover:!text-primary-foreground transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:!bg-muted disabled:hover:!text-foreground active:scale-95"
                        variant="outline"
                        onClick={handleIncrement}
                        disabled={value !== undefined && value >= max}
                        style={{
                            minWidth: "1.25rem",
                            height: "1rem",
                            padding: 0,
                        }}>
                        <ChevronUp size={12} />
                    </Button>
                    <Button
                        aria-label="Decrease value"
                        className="bg-muted text-foreground text-[11px] px-1 h-4 min-h-0 rounded-l-none rounded-tr-none border-input border-l-0 border-t-[0.5px] focus-visible:relative border-none cursor-pointer hover:!bg-primary hover:!text-primary-foreground transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:!bg-muted disabled:hover:!text-foreground active:scale-95"
                        variant="outline"
                        onClick={handleDecrement}
                        disabled={value !== undefined && value <= min}
                        style={{
                            minWidth: "1.25rem",
                            height: "1rem",
                            padding: 0,
                        }}>
                        <ChevronDown size={12} />
                    </Button>
                </div>
            </div>
        );
    }
);
